import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ImportResponse, ValidateXeroOrgsResponse } from '../../types'
import { importXeroInvoicesFromJson } from '../../services/licensing'
import { validateXeroOrganisations } from '../../services/admin'

export interface XeroImportFormController {
  isDragging: boolean
  selectedFile: File | null
  isUploading: boolean
  result: ImportResponse | null
  error: string | null
  fileInputRef: React.RefObject<HTMLInputElement | null>
  isValidating: boolean
  validationResult: ValidateXeroOrgsResponse | null
  handleDragOver: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleUpload: () => Promise<void>
  handleValidate: () => Promise<void>
  handleClear: () => void
}

interface UseXeroImportFormArgs {
  onSuccess?: ((result: ImportResponse) => void) | undefined
  onError?: ((error: Error) => void) | undefined
}

function isJsonFile(file: File): boolean {
  return file.type === 'application/json' || file.name.endsWith('.json')
}

interface FileSelectionState {
  selectedFile: File | null
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  isDragging: boolean
  fileInputRef: React.RefObject<HTMLInputElement | null>
  handleDragOver: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  acceptFile: (file: File | undefined) => void
}

function useFileSelection(onAccept: () => void): FileSelectionState {
  const { t } = useTranslation('admin')
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const acceptFile = useCallback(
    (file: File | undefined) => {
      if (file && isJsonFile(file)) {
        setSelectedFile(file)
        setError(null)
        onAccept()
      } else if (file) {
        setError(t('licensing.errors.pleaseUploadJson'))
      }
    },
    [t, onAccept]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      if (e.dataTransfer.files.length > 0) acceptFile(e.dataTransfer.files[0])
    },
    [acceptFile]
  )
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) acceptFile(files[0])
    },
    [acceptFile]
  )

  return {
    selectedFile,
    setSelectedFile,
    error,
    setError,
    isDragging,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
    acceptFile,
  }
}

interface ValidateFileDeps {
  file: File
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setValidationResult: React.Dispatch<React.SetStateAction<ValidateXeroOrgsResponse | null>>
  setIsValidating: React.Dispatch<React.SetStateAction<boolean>>
  t: ReturnType<typeof useTranslation<'admin'>>['t']
}

async function executeValidation(deps: ValidateFileDeps): Promise<void> {
  const { file, setError, setValidationResult, setIsValidating, t } = deps
  setIsValidating(true)
  setError(null)
  setValidationResult(null)
  try {
    const text = await file.text()
    const data = JSON.parse(text) as { invoices?: { xeroOrganisationId?: string }[] }
    if (!data.invoices || !Array.isArray(data.invoices))
      throw new Error(t('licensing.xero.errors.invalidJsonFormat'))
    const xeroOrgIds = [
      ...new Set(
        data.invoices.map((inv) => inv.xeroOrganisationId).filter((id): id is string => !!id)
      ),
    ]
    if (xeroOrgIds.length === 0) throw new Error(t('licensing.xero.errors.noXeroOrgIdsFound'))
    setValidationResult(await validateXeroOrganisations(xeroOrgIds))
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Failed to validate file')
  } finally {
    setIsValidating(false)
  }
}

export function useXeroImportForm({
  onSuccess,
  onError,
}: UseXeroImportFormArgs): XeroImportFormController {
  const { t } = useTranslation('admin')
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<ImportResponse | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<ValidateXeroOrgsResponse | null>(null)

  const fs = useFileSelection(useCallback(() => { setResult(null) }, []))

  const handleUpload = useCallback(async (): Promise<void> => {
    if (!fs.selectedFile) return
    setIsUploading(true)
    fs.setError(null)
    setResult(null)
    try {
      const response = await importXeroInvoicesFromJson(fs.selectedFile)
      setResult(response)
      onSuccess?.(response)
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('licensing.errors.failedToImportFile')
      fs.setError(msg)
      onError?.(err instanceof Error ? err : new Error(msg))
    } finally {
      setIsUploading(false)
    }
  }, [fs, onSuccess, onError, t])

  const handleClear = useCallback((): void => {
    fs.setSelectedFile(null)
    setResult(null)
    fs.setError(null)
    setValidationResult(null)
    if (fs.fileInputRef.current) fs.fileInputRef.current.value = ''
  }, [fs])

  const handleValidate = useCallback(async (): Promise<void> => {
    if (!fs.selectedFile) return
    await executeValidation({ file: fs.selectedFile, setError: fs.setError, setValidationResult, setIsValidating, t })
  }, [fs, t])

  return {
    isDragging: fs.isDragging,
    selectedFile: fs.selectedFile,
    isUploading,
    result,
    error: fs.error,
    fileInputRef: fs.fileInputRef,
    isValidating,
    validationResult,
    handleDragOver: fs.handleDragOver,
    handleDragLeave: fs.handleDragLeave,
    handleDrop: fs.handleDrop,
    handleFileSelect: fs.handleFileSelect,
    handleUpload,
    handleValidate,
    handleClear,
  }
}
